// Donate-wizard state (arc 101 slice 11, plan doc 05) — pure, so the one rule
// that matters is provable without rendering a dialog.
//
// **The lock is the server's answer, never the redirect.** Stripe sends the
// donor back to `?donated=1`, and that parameter proves nothing: a donor can
// type it, a bookmark can carry it, and a closed tab never sends it at all. The
// subscription becomes real when `customer.subscription.created` reaches the
// worker and the seat sync writes the `oss_donor` tier — so steps 3-4 read that
// tier (plus "not past due"), which is exactly what `canFundDonation`
// (`packages/billing/src/tiers.ts`) fences the API with. Mirroring the server
// rule keeps the wizard from offering a step whose POST would 402.
//
// The mirror hides a step that would plainly refuse; it authorizes nothing. A
// crafted request still meets the same gate, and its 402 detail is the sentence
// the wizard renders.

import type { DonationRow } from './donations';

/** The wizard's four steps, in order. */
export const DONATE_STEPS = ['repo', 'pay', 'box', 'key'] as const;
export type DonateStep = (typeof DONATE_STEPS)[number];

/** Reachability of one step: finished, available now, or waiting on an earlier one. */
export type StepState = 'done' | 'open' | 'locked';

/**
 * Billing tier that IS a live $50/mo donor subscription. The Stripe seat sync
 * writes it from the donor price, so it reads real money — see `canFundDonation`.
 */
export const DONOR_TIER = 'oss_donor';

/**
 * The two server facts the funding rule reads: the account's tier
 * (`GET /v1/billing/entitlements`) and whether collection stopped after a failed
 * payment (`GET /v1/billing/usage`). Split across two endpoints today; folded
 * here so the predicate below states the rule once.
 */
export type DonorFunding = {
  readonly tier: string;
  readonly billingPaused: boolean;
};

/**
 * Is the donor subscription live RIGHT NOW? Client mirror of `canFundDonation`:
 * the donor tier AND not past due — a lapsed card stops us operating donated
 * resources, so attaching another one is refused (`donor_subscription_unpaid`).
 *
 * `null`/`undefined` (either read still in flight, or failed) is NOT active: an
 * unlocked step during a load would let a donor start attaching a box the API
 * would then refuse.
 */
export const donorSubscriptionActive = (funding: DonorFunding | null | undefined): boolean =>
  funding !== null &&
  funding !== undefined &&
  funding.tier === DONOR_TIER &&
  !funding.billingPaused;

/** What the wizard already holds — the input every step-state question reads. */
export type WizardProgress = {
  /** The validated `owner/repo` from step 1; null until the server confirmed one. */
  readonly project: string | null;
  /** The server says the donor subscription is live (see {@link donorSubscriptionActive}). */
  readonly funded: boolean;
  /** A live `compute` gift already pinned to {@link project}. */
  readonly boxGiven: boolean;
  /** A live `llm` gift already pinned to {@link project}. */
  readonly keyGiven: boolean;
};

/**
 * Where one step stands. Steps 3-4 are siblings, not a sequence: each is
 * individually completable, so giving a box never locks the key step and a donor
 * who gave only one comes back to give the other (`done` on the one they
 * finished, `open` on the one they didn't).
 */
export function stepState(step: DonateStep, p: WizardProgress): StepState {
  switch (step) {
    case 'repo':
      return p.project === null ? 'open' : 'done';
    case 'pay':
      if (p.funded) return 'done';
      return p.project === null ? 'locked' : 'open';
    case 'box':
      if (p.boxGiven) return 'done';
      return p.project !== null && p.funded ? 'open' : 'locked';
    case 'key':
      if (p.keyGiven) return 'done';
      return p.project !== null && p.funded ? 'open' : 'locked';
  }
}

/**
 * The step a returning donor should land on: the first that isn't finished, and
 * the last one when everything is. Resumption, not restart — a donor who paid
 * and enrolled a box reopens the wizard on the key step.
 */
export function firstOpenStep(p: WizardProgress): DonateStep {
  return DONATE_STEPS.find((s) => stepState(s, p) !== 'done') ?? 'key';
}

/** A live gift is one that is `active` and was never revoked — a parked row gives nothing. */
const live = (row: DonationRow): boolean => row.active && row.revoked_iso === null;

/** Rows pinned to one project, by the name the API resolved for the gift. */
const pinnedTo = (rows: readonly DonationRow[], fullName: string): readonly DonationRow[] =>
  rows.filter((r) => r.target_kind === 'project' && r.target_repo_full_name === fullName);

/**
 * What this donor has LIVE on one project. Read off the rows rather than the
 * POST echoes: a `money` gift comes back `active: false` and only Stripe's
 * webhook flips it, so the list is the one place that knows.
 */
export function liveGifts(
  rows: readonly DonationRow[],
  fullName: string,
): { readonly money: boolean; readonly box: boolean; readonly key: boolean } {
  const mine = pinnedTo(rows, fullName).filter(live);
  return {
    money: mine.some((r) => r.kind === 'money'),
    box: mine.some((r) => r.kind === 'compute'),
    key: mine.some((r) => r.kind === 'llm'),
  };
}

/**
 * Projects this donor PAID for and attached nothing to — the "finish your
 * donation" prompt's whole population.
 *
 * Attached NOTHING, deliberately: a box without a key (or the reverse) is a
 * legitimate gift someone chose to stop at, and nagging them for the other half
 * forever would be wrong. Zero resources, though, is a subscription paying for
 * capacity that does not exist — which is the failure this prompt catches.
 */
export function unfinishedProjects(rows: readonly DonationRow[]): readonly string[] {
  const paid = rows
    .filter((r) => live(r) && r.kind === 'money' && r.target_kind === 'project')
    .map((r) => r.target_repo_full_name)
    // A pinned gift whose repo the projection could not name (the row was
    // deleted) is unactionable here — there is nothing to reopen the wizard at.
    .filter((name): name is string => name !== undefined && name !== '');
  return [...new Set(paid)].filter((name) => {
    const gifts = liveGifts(rows, name);
    return !gifts.box && !gifts.key;
  });
}
